/**
 * Globalisation
 * =============
 *
 * Initialises everything for currency conversion, number formatting and dates.
 *
 * Based on locale.
 *
 * Uses jQuery Globalise library (https://github.com/jquery/globalize)
 *
 * The files for each locale are hand picked from jQuery Globalise and added to globalisation folder.
 * To get new files for other languages, git clone (https://github.com/jquery/globalize)
 * run npm install, bower install and grunt. This will generate an export with all the files to pick from.
 *
 * We don't need them all and the build task to copy 12000+ json files takes to long.
 * Any new language should be manually added.
 */
'use strict';

import {isArray} from 'underscore';
import {getLogger} from 'loglevel';
import * as globalize from 'globalize';
import {ARROW_CONFIG} from '../config/index';

const log = getLogger('Globalisation');

export class Globalisation {

  initLocale(localeCode) {
    if (localeCode !== ARROW_CONFIG.locale.code) {
      log.warn(`Initializing locale (${localeCode}) that is different to config (${ARROW_CONFIG.locale.code})`);
    } else {
      log.debug(`Initializing locale to ${localeCode}`);
    }
    globalize.locale(localeCode);
  }

  /**
   * Load the CLDR Data containing information for dates and numbers
   * See docs for format (https://github.com/jquery/globalize/blob/master/doc/api/core/load.md)
   *
   * This data is requested from the server as the page is initialised
   *
   * @param cldrData
   */
  loadCldrData(cldrData) {
    if (isArray(cldrData)) {
      globalize.load.apply(globalize, cldrData);
    } else if (cldrData) {
      globalize.load(cldrData);
    }
  }

  /**
   * Given an object of domain/key/value pairs load them
   * into globalize for a particular locale
   * @param localeCode [String] en or de
   * @param translations [Object] { domain: { key: value } }
   */
  loadTranslationsForLocale(localeCode, translations) {
    let messages = {};
    messages[localeCode] = translations;
    globalize.loadMessages(messages);
  }

  pluraliseAndTranslate(domain, key, item) {
    var message, result;
    try {
      message = globalize.messageFormatter([domain, key]);
      result = message(item);
    } catch (e) {
      console.error('Unable to translate / pluralise:', domain, key, e);
      result = '[' + key + ']'; // Do nothing
    }
    return result;
  }

  formatNumber(value) {
    var result;
    try {
      result = globalize.formatNumber(parseFloat(value));
    } catch (e) {
      console.error('Unable to format number:', value, e);
      result = value; // Do nothing
    }
    return result;
  }

  formatCurrency(value, minimumFractionDigits, maximumFractionDigits) {
    let result;
    let options = {
      minimumFractionDigits: minimumFractionDigits || 2,
      maximumFractionDigits: maximumFractionDigits || 4
    };
    try {
      result = globalize.currencyFormatter(ARROW_CONFIG.locale.currencyIsoCode, options)(parseFloat(value));
    } catch (e) {
      console.error('Unable to format currency:', value, e);
      result = value; // Do nothing
    }
    return result;
  }

  formatCurrencyWithIso(value, isoCode, minimumFractionDigits, maximumFractionDigits) {
    let result;
    let options = {
      minimumFractionDigits: minimumFractionDigits || 2,
      maximumFractionDigits: maximumFractionDigits || 4
    };
    try {
      result = globalize.currencyFormatter(isoCode, options)(parseFloat(value));
    } catch (e) {
      console.error('Unable to format currency:', value, e);
      result = value; // Do nothing
    }
    return result;
  }

  parseNumber(value) {
    return globalize.numberParser()(value);
  }

  //remove trailing zeros from a decimal that has 4 digits
  //provide at least 2 digits at all times
  stripZeros(value = '') {
    value = value.toString();
    let result;
    let decimals = value.substr(value.length - 4);
    for (var i = 3; i > 1; i--) {
      if (decimals[i] === '0') {
        decimals = decimals.slice(0, i);
      } else {
        break;
      }
    }
    result = value.replace(value.substr(value.length - 4, value.length - 1), decimals);
    return result;
  }

}



// WEBPACK FOOTER //
// ./uikit/utilities/Globalisation.js